import { Link } from "react-router-dom";
import { useAuth, STAFF_ROLES } from "@/context/AuthContext";
import { Logo, useBranding } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { fileUrl } from "@/lib/api";
import { ChevronRight, HardHat, Gauge, Award, TrendingUp, ClipboardCheck, Users } from "lucide-react";

const JOURNEY = [
  { name: "APPLY", desc: "Register your interest and tell us about yourself.", icon: ClipboardCheck, imgKey: "apply_url" },
  { name: "ASSESS", desc: "Online aptitude assessment, simulator session and assessment day.", icon: Gauge, imgKey: "simulator_url", caption: "Simulator Aptitude & Control Assessment" },
  { name: "TRAIN", desc: "Paid 10-day Lomond Operator Training Camp.", icon: HardHat, imgKey: "train_url" },
  { name: "QUALIFY", desc: "2-week external 360 excavator course and assessment.", icon: Award, imgKey: "qualify_url" },
  { name: "DEVELOP", desc: "Paid live site work as a Development Operator.", icon: Users, imgKey: "develop_url" },
  { name: "PROGRESS", desc: "Build seat hours towards Lomond Competent Operator.", icon: TrendingUp, imgKey: "progress_url" },
];

const VALUE_POINTS = ["Paid training", "Recognised qualifications", "Real plant", "Real career progression"];

export default function Home() {
  const { user } = useAuth();
  const branding = useBranding();
  const focal = branding.focal || {};
  const fit = branding.fit || {};
  const hero = branding.hero_url ? fileUrl(branding.hero_url) : null;
  const dashLink = user ? (STAFF_ROLES.includes(user.role) ? "/staff" : "/dashboard") : null;

  const stepImg = (key) => (branding[key] ? fileUrl(branding[key]) : null);

  return (
    <div className="min-h-screen bg-obsidian text-foreground" data-testid="home-page">
      <header className="absolute top-0 inset-x-0 z-30 bg-white border-b border-steel">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            {user ? (
              <Link to={dashLink}>
                <Button className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="header-dashboard-btn">
                  My Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button variant="outline" className="border-charcoal/30 text-charcoal hover:bg-lomond hover:text-black hover:border-lomond font-semibold" data-testid="header-login-btn">
                  Log in
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      <section className="relative min-h-[92vh] flex items-end" data-testid="hero-section">
        {hero ? (
          <img src={hero} alt="Lomond excavator with breaker attachment at work"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: focal.hero_url || "50% 40%" }}
            data-testid="hero-image" />
        ) : (
          <div className={`absolute inset-0 bg-elevated ${branding.loaded ? "" : "animate-pulse"}`} data-testid="hero-placeholder" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pb-16 pt-40 w-full">
          <div className="hazard-stripes h-2 w-24 mb-6 rounded-sm" />
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFC800] mb-4" data-testid="hero-eyebrow">
            Lomond Operator Academy
          </p>
          <h1 className="font-heading font-black uppercase tracking-tight leading-none text-white text-4xl sm:text-5xl lg:text-6xl max-w-3xl" data-testid="hero-heading">
            Build a Career in Plant Operation
          </h1>
          <p className="mt-5 text-white/85 text-base sm:text-lg max-w-xl leading-relaxed" data-testid="hero-subtext">
            No experience needed. We select for attitude, train you properly, pay you from day one
            and back you all the way to qualified plant operator.
          </p>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
            {VALUE_POINTS.map((v) => (
              <span key={v} className="flex items-center gap-2 text-sm font-semibold text-white" data-testid={`value-point-${v.split(" ")[0].toLowerCase()}`}>
                <span className="h-2 w-2 bg-lomond rounded-sm" /> {v}
              </span>
            ))}
          </div>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/register">
              <Button size="lg" className="w-full sm:w-auto bg-lomond text-black font-heading font-extrabold uppercase tracking-wide hover:bg-[#E6B400] text-base px-8" data-testid="register-interest-btn">
                Register Interest <ChevronRight className="ml-1 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-white/60 text-white bg-black/20 hover:bg-white hover:text-black font-heading font-extrabold uppercase tracking-wide text-base px-8" data-testid="applicant-login-btn">
                Login
              </Button>
            </Link>
          </div>
          <p className="mt-4 text-xs text-white/60 font-mono uppercase tracking-widest">
            One login — the Academy takes you to the right place
          </p>
        </div>
      </section>

      <section className="bg-white border-y border-steel py-16 sm:py-24" data-testid="journey-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-lomond mb-2">Your Journey</p>
              <h2 className="font-heading font-extrabold uppercase tracking-tight text-charcoal text-2xl sm:text-3xl lg:text-4xl">
                From Applicant to Competent Operator
              </h2>
            </div>
            <p className="text-slate-500 text-sm max-w-sm">
              A structured, paid pathway. Every stage assessed, evidenced and signed off.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {JOURNEY.map((step, i) => {
              const img = step.imgKey ? stepImg(step.imgKey) : null;
              const contain = step.imgKey && fit[step.imgKey] === "contain";
              return (
                <div key={step.name}
                  className="relative rounded-md border border-steel bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-md hover:border-lomond"
                  style={{ animationDelay: `${i * 80}ms` }}
                  data-testid={`journey-step-${step.name.toLowerCase()}`}>
                  <div className="flex items-center justify-between mb-4">
                    <step.icon className="h-7 w-7 text-charcoal" />
                    <span className="font-mono text-xs text-slate-400">0{i + 1}</span>
                  </div>
                  <h3 className="font-heading font-extrabold uppercase tracking-wide text-charcoal text-lg">{step.name}</h3>
                  <p className="text-sm text-slate-500 mt-1 leading-relaxed">{step.desc}</p>
                  {img ? (
                    <div className="mt-4 relative rounded-sm overflow-hidden border border-steel bg-white" data-testid={`journey-img-${step.name.toLowerCase()}`}>
                      <img src={img} alt={`${step.name} — Lomond Operator Academy`}
                        className={`w-full h-36 ${contain ? "object-contain p-2" : "object-cover"}`}
                        style={{ objectPosition: focal[step.imgKey] || "50% 50%" }} />
                      {step.caption && (
                        <div className="absolute inset-x-0 bottom-0 bg-black/70 px-3 py-1.5">
                          <p className="text-[10px] font-mono uppercase tracking-widest text-[#FFC800]">{step.caption}</p>
                        </div>
                      )}
                    </div>
                  ) : (!branding.loaded && step.imgKey) ? (
                    <div className="mt-4 h-36 rounded-sm border border-steel bg-elevated animate-pulse" data-testid={`journey-img-skeleton-${step.name.toLowerCase()}`} />
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 items-stretch">
          <div className="relative rounded-md overflow-hidden border border-steel order-2 lg:order-1 min-h-[20rem] lg:min-h-0" data-testid="why-image-panel">
            {branding.why_url ? (
              <img src={fileUrl(branding.why_url)}
                alt="Lomond Plant Group operative in branded hi-vis"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ objectPosition: focal.why_url || "50% 60%" }}
                data-testid="why-image" />
            ) : (
              <div className={`absolute inset-0 bg-elevated ${branding.loaded ? "" : "animate-pulse"}`} data-testid="why-image-placeholder" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <p className="font-heading font-bold uppercase text-white text-lg">You're building a career,</p>
              <p className="text-[#FFC800] font-mono text-xs uppercase tracking-widest">not just getting a ticket</p>
            </div>
          </div>
          <div className="order-1 lg:order-2 self-center">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-lomond mb-2">Why Lomond</p>
            <h2 className="font-heading font-extrabold uppercase tracking-tight text-charcoal text-2xl sm:text-3xl lg:text-4xl mb-6">
              Selected. Trained. Qualified. Developed.
            </h2>
            <div className="space-y-5">
              {[
                ["No experience required", "We assess aptitude, attitude and safety mindset — not what you already know."],
                ["Paid from day one", "Trainee Plant Operator wage from your first day of the Training Camp."],
                ["Real machines, real sites", "Lomond's own fleet, instructors and live civil engineering work."],
                ["52 weeks of structured development", "Funding-backed action plans and evidence from start to Competent Operator."],
              ].map(([t, d]) => (
                <div key={t} className="flex gap-4">
                  <span className="mt-1 h-3 w-3 shrink-0 bg-lomond rounded-sm" />
                  <div>
                    <h3 className="font-heading font-bold uppercase tracking-wide text-charcoal text-base">{t}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{d}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/register">
              <Button className="mt-8 bg-lomond text-black font-heading font-extrabold uppercase tracking-wide hover:bg-[#E6B400]" data-testid="cta-register-bottom">
                Start Your Application <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="bg-white border-t border-steel py-10" data-testid="home-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="inline-flex items-center gap-3">
            {branding.logo_url ? (
              <img src={fileUrl(branding.logo_url)} alt="Lomond" className="h-8 w-auto" data-testid="footer-logo" />
            ) : (
              <span className="bg-lomond text-black font-heading font-black uppercase tracking-wider px-2.5 py-1 rounded-sm leading-none">LOMOND</span>
            )}
            <span className="font-heading font-bold uppercase tracking-widest text-charcoal text-sm">Operator Academy</span>
          </span>
          <p className="text-xs text-[#666666] font-mono uppercase tracking-widest text-center">
            Lomond Plant Ltd — Operator Academy — Build a Career in Plant Operation
          </p>
        </div>
      </footer>
    </div>
  );
}
